import { useState } from 'react'
import { printZpl, getSavedDpi, ERROR_CODES } from '../utils/labelPrinter'
import { useToast } from './useToast'

const API_BASE = import.meta.env.VITE_API_URL || ''

// Bulk jobs go to the printer one at a time with a pause between sends —
// firing them in parallel lets Browser Print reorder the queue.
const BULK_DELAY_MS = 750

/**
 * Print-state machine for product label printing (Draft Manager, and any
 * future table that grows a Print Label column).
 *
 * Exposes per-row states (idle → printing → printed | failed), a sequential
 * bulk printer that preserves table order, and the download-fallback dialog
 * payload for when Zebra Browser Print isn't running.
 */
export function useLabelPrinting() {
  const [printStates, setPrintStates] = useState({})
  const [fallback, setFallback] = useState(null) // { zpl, filename }
  const [bulkPrinting, setBulkPrinting] = useState(false)
  const { toast } = useToast()

  function rowState(id) {
    return printStates[id] || 'idle'
  }

  function setRowState(id, state) {
    setPrintStates(prev => {
      if (state === 'idle') {
        const { [id]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [id]: state }
    })
  }

  async function printOne(draft) {
    setRowState(draft.id, 'printing')

    let zpl
    try {
      zpl = await fetchZpl(draft.id)
    } catch (err) {
      setRowState(draft.id, 'failed')
      toast.error(`Label fetch failed: ${err.message}`)
      return false
    }

    try {
      await printZpl(zpl)
      setRowState(draft.id, 'printed')
      toast.success(`Label sent to printer`)
      return true
    } catch (err) {
      setRowState(draft.id, 'failed')
      if (err.code === ERROR_CODES.AGENT_NOT_RUNNING) {
        setFallback({ zpl, filename: `label-${draft.id}.zpl` })
      } else {
        // Distinguishable on the shop floor: "No Zebra printer found — check
        // the USB cable and power" vs "Printer rejected the job (...)".
        toast.error(err.message)
      }
      return false
    }
  }

  async function printSelected(drafts) {
    if (drafts.length === 0 || bulkPrinting) return
    setBulkPrinting(true)
    let printed = 0
    try {
      for (let i = 0; i < drafts.length; i++) {
        const draft = drafts[i]
        setRowState(draft.id, 'printing')

        let zpl
        try {
          zpl = await fetchZpl(draft.id)
        } catch (err) {
          setRowState(draft.id, 'failed')
          toast.error(`${shortTitle(draft)}: label fetch failed: ${err.message}`)
          continue
        }

        try {
          await printZpl(zpl)
          setRowState(draft.id, 'printed')
          printed++
        } catch (err) {
          setRowState(draft.id, 'failed')
          if (err.code === ERROR_CODES.AGENT_NOT_RUNNING) {
            await offerBulkDownload(zpl, drafts.slice(i + 1))
            return
          }
          toast.error(`${shortTitle(draft)}: ${err.message}`)
        }

        if (i < drafts.length - 1) await delay(BULK_DELAY_MS)
      }
      if (printed > 0) {
        toast.success(`Printed ${printed} of ${drafts.length} label${drafts.length === 1 ? '' : 's'}`)
      }
    } finally {
      setBulkPrinting(false)
    }
  }

  // Agent went missing mid-batch: bundle this and every remaining label into
  // one .zpl (multiple ^XA…^XZ documents pipe to lp as one job) so the
  // operator still gets all their labels.
  async function offerBulkDownload(firstZpl, remainingDrafts) {
    const rest = await Promise.all(
      remainingDrafts.map(d => fetchZpl(d.id).catch(() => null))
    )
    remainingDrafts.forEach(d => setRowState(d.id, 'idle'))
    const documents = [firstZpl, ...rest.filter(Boolean)]
    setFallback({
      zpl: documents.join('\n'),
      filename: documents.length > 1 ? `labels-${documents.length}-items.zpl` : 'label.zpl'
    })
  }

  return {
    rowState,
    printOne,
    printSelected,
    bulkPrinting,
    fallback,
    clearFallback: () => setFallback(null)
  }
}

/**
 * Fetch ZPL as raw text. Deliberately not the shared api client: that client
 * JSON-parses every body, and this endpoint returns text/plain.
 */
async function fetchZpl(productId) {
  const dpi = getSavedDpi()
  const response = await fetch(
    `${API_BASE}/api/labels/product/${productId}.zpl?dpi=${dpi}`,
    { credentials: 'include' }
  )
  const text = await response.text()
  if (!response.ok) {
    throw new Error(extractError(text) || `HTTP error ${response.status}`)
  }
  return text
}

function extractError(text) {
  try {
    return JSON.parse(text)?.error || null
  } catch {
    return null
  }
}

function shortTitle(draft) {
  const title = draft.title || `Product ${draft.id}`
  return title.length > 40 ? `${title.slice(0, 40)}…` : title
}

function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}
